import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getThinkChat, sendThinkMessage, clearThinkChat, formatApiError } from "@/lib/inmindApi";

const DEFAULT_PROMPTS = [
  "Help me sharpen my own theory about this",
  "Explain this in plainer words",
  "Give me a real-life example",
  "What might I be missing here?",
];

/**
 * ThinkPanel — AI helper bound to a context.
 *
 * Default behavior: term-scoped (uses /api/me/think/{termId}).
 * Pass `api={ get, send, clear }` to wire a different scope (e.g. personal notes).
 * `api` may be a new object on every render — we hold it in a ref so the effect
 * doesn't loop. Use the parent `key` prop to remount when the bound resource changes.
 */
export default function ThinkPanel({
  termId, termName, api,
  title, subtitle, prompts = DEFAULT_PROMPTS, emptyHint, placeholder,
}) {
  const apiRef = useRef(api);
  apiRef.current = api;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = apiRef.current?.get || (() => getThinkChat(termId));
      const data = await fn();
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Think");
    } finally {
      setLoading(false);
    }
  }, [termId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const handleSend = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    const tempId = "tmp-" + Date.now();
    setMessages((prev) => [...prev, { id: tempId, role: "user", content, created_at: new Date().toISOString() }]);
    try {
      const fn = apiRef.current?.send || ((c) => sendThinkMessage(termId, c));
      const res = await fn(content);
      setMessages((prev) => {
        const without = prev.filter((m) => m.id !== tempId);
        return [...without, res.user_message, res.assistant_message];
      });
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      toast.error(formatApiError(e.response?.data?.detail) || "Think couldn't reply");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const doClear = async () => {
    if (!confirm("Clear this conversation with Think?")) return;
    try {
      const fn = apiRef.current?.clear || (() => clearThinkChat(termId));
      await fn();
      setMessages([]);
      toast.success("Cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't clear");
    }
  };

  const empty = !loading && messages.length === 0;
  const heading = subtitle || (termName || "Refine your own theories");
  const eyebrow = title || "Think · your AI thinking partner";

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-black/5 shadow-[0_4px_18px_rgba(0,0,0,0.03)]" data-testid="think-panel">
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-black/5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[var(--inmind-primary)]/12 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
              {eyebrow}
            </div>
            <div className="font-serif text-base text-[var(--inmind-text)] truncate" data-testid="think-title">
              {heading}
            </div>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={doClear}
            data-testid="think-clear"
            aria-label="Clear chat"
            title="Clear conversation"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[var(--inmind-muted)] hover:bg-black/5 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
          </button>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3 min-h-[280px]" data-testid="think-scroll">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-[var(--inmind-muted)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : empty ? (
          <div className="py-2" data-testid="think-empty">
            <p className="text-sm text-[var(--inmind-text-secondary)] leading-relaxed">
              {emptyHint || "Share your own theory, gut feeling, or question about today's card. Think will help you sharpen and expand the idea — not just explain it back."}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {prompts.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSend(s)}
                  data-testid={`think-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-[var(--inmind-card-subtle)] text-[var(--inmind-text-secondary)] hover:bg-black/5 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} />)
        )}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-[var(--inmind-muted)] pl-1" data-testid="think-typing">
            <Loader2 className="w-3 h-3 animate-spin" /> Think is thinking…
          </div>
        )}
      </div>

      <div className="border-t border-black/5 p-3 sm:p-4">
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder || "Share your theory or ask a follow-up…"}
            data-testid="think-input"
            className="resize-none rounded-xl border-black/10 min-h-[44px] max-h-32 text-sm"
            rows={1}
          />
          <Button
            onClick={() => handleSend()}
            disabled={sending || !input.trim()}
            data-testid="think-send"
            aria-label="Send"
            className="rounded-xl h-11 px-3.5 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors shrink-0"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.8} />}
          </Button>
        </div>
        <div className="text-[10px] text-[var(--inmind-muted)] mt-1.5 px-1">
          Enter to send · Shift+Enter for new line
        </div>
      </div>
    </div>
  );
}

function Bubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`think-msg-${m.role}`}>
      <div
        className={`max-w-[88%] sm:max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[var(--inmind-primary)] text-white rounded-br-md"
            : "bg-[var(--inmind-card-subtle)] text-[var(--inmind-text)] rounded-bl-md"
        }`}
      >
        {m.content}
      </div>
    </div>
  );
}
